import React, { useCallback, useEffect, useState } from "react";

import { observer } from "mobx-react-lite";
import { Button, Icon } from "@material-ui/core";

import { useStore } from "../../stores/helpers/useStore";
import { apiErrorMessage } from "../../helpers/apiErrorMessage";

import { getSubscriptionPrice } from "../../helpers/getSubscriptionPrice";

import OftadehLayout from "../../components/OftadehLayout/OftadehLayout";
import CreateSubscription from "./Stages/CreateSubscription";
import UpdateSubscription from "./Stages/UpdateSubscription/UpdateSubscription";
import ViewSubscription from "./Stages/ViewSubscription/ViewSubscription";
import StripeReturnHandler from "./StripeReturnHandler";

const SubscriptionPage = (props) => {
	const {
		authStore: { email },
		subscriptionStore: {
			subscriptionQuantity,
			nextCycleQuantity,
			subscriptionStatus,
			subscriptionTiers,
			subscriptionEnd,
			subscriptionApprovalLink,
			subscriptionSource,
			billingStatus,
			getSubscriptionInfo,
			getSubscriptionPlanInfo,
			createAdminSubscription,
			updateAdminSubscription,
			cancelAdminSubscription,
			createStripeCheckout,
			previewStripeQuantity,
			openBillingPortal,
			pollStripeSession,
		},
		hostsStore: { hostsList, getListOfHosts },
		uiStore: { loading },
	} = useStore();

	const [stage, setStage] = useState("VIEW"); // VIEW | CREATE | UPDATE
	const { history } = props;
	const [error, setError] = useState(null);
	const [notice, setNotice] = useState("");

	const urlParams = new URLSearchParams(window.location.search);
	const stripeSessionId = urlParams.get("stripe_session_id");

	const getSubscription = useCallback(async () => {
		setError(null);
		try {
			await getSubscriptionInfo({ force: true });
		} catch (err) {
			setError(
				apiErrorMessage(
					err,
					"Couldn't load your subscription. Refresh the page and try again."
				)
			);
		}
		try {
			await getSubscriptionPlanInfo();
		} catch (err) {
			console.error("Subscription plan lookup failed", err);
		}
	}, [getSubscriptionPlanInfo, getSubscriptionInfo]);

	useEffect(() => {
		getSubscription();
	}, [getSubscription]);

	useEffect(() => {
		getListOfHosts();
	}, [getListOfHosts]);

	if (stripeSessionId) {
		return (
			<OftadehLayout loading={false}>
				<StripeReturnHandler
					sessionId={stripeSessionId}
					pollStripeSession={pollStripeSession}
					onComplete={() => {
						window.history.replaceState({}, "", "/subscription");
						getSubscription();
					}}
					onError={() => {
						window.history.replaceState({}, "", "/subscription");
					}}
				/>
			</OftadehLayout>
		);
	}

	const showLoader = loading && !subscriptionStatus && !error;

	return (
		<OftadehLayout loading={showLoader}>
			{error && !subscriptionStatus ? (
				<div className="subscription-page">
					<div className="subscription-empty-state">
						<Icon className="subscription-empty-icon">error_outline</Icon>
						<h2 className="subscription-empty-title">Couldn't load subscription</h2>
						<p className="subscription-empty-description">{error}</p>
						<Button
							variant="contained"
							color="primary"
							className="subscription-button primary"
							onClick={getSubscription}
						>
							Try again
						</Button>
					</div>
				</div>
			) : (
				{
					VIEW: (
						<ViewSubscription
							subscriptionQuantity={subscriptionQuantity}
							nextCycleQuantity={nextCycleQuantity}
							subscriptionEnd={subscriptionEnd}
							subscriptionStatus={subscriptionStatus}
							subscriptionApprovalLink={subscriptionApprovalLink}
							subscriptionSource={subscriptionSource}
							billingStatus={billingStatus}
							notice={notice}
							clearNotice={() => setNotice("")}
							cancelAdminSubscription={cancelAdminSubscription}
							openBillingPortal={openBillingPortal}
							createSubscription={() => setStage("CREATE")}
							updateSubscription={() => setStage("UPDATE")}
							getPrice={getSubscriptionPrice}
						/>
					),
					CREATE: (
						<CreateSubscription
							subscriptionQuantity={subscriptionQuantity}
							subscriptionEnd={subscriptionEnd}
							subscriptionStatus={subscriptionStatus}
							createAdminSubscription={createAdminSubscription}
							createStripeCheckout={createStripeCheckout}
							pollStripeSession={pollStripeSession}
							getPrice={getSubscriptionPrice}
							back={() => setStage("VIEW")}
						/>
					),
					UPDATE: (
						<UpdateSubscription
							subscriptionQuantity={subscriptionQuantity}
							nextCycleQuantity={nextCycleQuantity}
							subscriptionSource={subscriptionSource}
							subscriptionTiers={subscriptionTiers}
							updateAdminSubscription={updateAdminSubscription}
							previewStripeQuantity={previewStripeQuantity}
							email={email}
							getPrice={getSubscriptionPrice}
							existingHostsQuantity={hostsList.length}
							back={() => setStage("VIEW")}
							onUpdated={(message) => {
								setNotice(message || "Subscription updated.");
								setStage("VIEW");
							}}
							goToHostsList={() => history.push("/hosts")}
						/>
					),
				}[stage]
			)}
		</OftadehLayout>
	);
};

export default observer(SubscriptionPage);
